import { Checkbox, IconButton, PrimaryButton } from '@fluentui/react';
import { appState } from "@v2/app-state";
import { FormControl } from '@v2/components/controls/FormControl/FormControl';
import { ValidationError } from '@v2/components/controls/ValidationError/ValidationError';
import HSObjectPicker from '@v2/components/controls/implementations/HSObjectPicker';
import {
  ExecutionConfirmationDialog
} from "@v2/components/dialogs/ExecutionConfirmationDialog/ExecutionConfirmationDialog";
import { PropertyEdit } from '@v2/components/flowsteps/editors/hubspot/objects/PullOjbectHS/PropertyEdit/PropertyEdit';
import { PushObjectsHSState } from '@v2/components/flowsteps/editors/hubspot/objects/PushObjectsHS/push-objects-hs-state';
import { AdvancedSettings } from '@v2/components/flowsteps/editors/common/AdvancedSettings/AdvancedSettings';
import { FlowstepButtons } from '@v2/components/flowsteps/editors/common/FlowstepButtons/FlowstepButtons';
import { FlowstepContainer } from '@v2/components/flowsteps/editors/common/FlowstepContainer/FlowstepContainer';
import { MappingManager } from '@v2/components/flowsteps/editors/common/MappingManager/MappingManager';
import TitledPage from '@v2/components/layout/TitledPage';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Operation } from 'shared/types/flowstep';
import { FlowstepType, RangeType } from 'shared/utils/flowstep';
import RangeSelector from 'src/components/Flowsteps/PushData/RangeSelector';
import GroupPane from 'src/components/base/GroupPane';
import { ModalPage } from 'src/components/base/Page';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import { runShortAction } from 'src/services/utils';
import { useStores } from 'src/v2/hooks/useStores';
import { useStyles } from './PushObjectsHS.styles';
import UnsavedChangesDialog from "@v2/hooks/UnsavedChangesDialog";
import { renderEditorForType } from 'src/v2/components/controls/editors/hs-editors';

interface PushObjectsHSProps {
  id?: string;
  operation: Operation;
  rangeToApply?: typeof RangeType[keyof typeof RangeType];
  createNew?: boolean;
}

export const PushObjectsHS: React.FC<PushObjectsHSProps> = observer(({ id, createNew, operation, rangeToApply }) => {
  const styles = useStyles();
  const { officeStore, requestStore } = useStores();

  const [state] = useState(() => new PushObjectsHSState(operation, rangeToApply));

  const [isMappingManagerVisible, setMappingManagerVisible] = useState<boolean>(false);
  const [optionsCollapsed, setOptionsCollapsed] = useState<boolean>(true);
  const [advancedCollapsed, setAdvancedCollapsed] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      await state.loadFlowstep(id, createNew);
    })();
  }, [id, createNew]);

  useEffect(() => {
    const error = state.loadingObjectFieldsError || state.loadingObjectTypesError;
    if (error) {
      requestStore.raise(error, true)
    }
  }, [state.loadingObjectFieldsError, state.loadingObjectTypesError]);

  let sectionNumber = 1;

  //Todo: create a function to get the title
  const title = `${state.isNewFlowstep ? 'New: ' : ''}${operation === Operation.INSERT ? 'Insert into' : 'Update'} ${state.selectedObjectType?.name || 'Objects'}`;

  const handleDetectClick = () => {
    (async () =>
      await runShortAction(async () => {
        await state.detectRange();
        if (!state.flowstep?.id) {
          await state.findAndLoadSimilarFlowstep(false);
        }
      }
      ))();
  }

  const handleRangeChange = (value: any, isValid: boolean) => {
    state.rangeToApply = value;
    state.isValidRangeToApply = isValid;
  }

  const getTimestampSheetId = () => {
    return state.flowstep?.options.worksheetId ?? officeStore.activeWorksheetId;
  }

  const handleRunClick = async () => {
    const flowstep = state.buildFlowstep();

    const isConfirmed = await appState.showDialog((setResult) =>
      <ExecutionConfirmationDialog
        onResult={(value) => setResult(value)}
        flowstep={flowstep}
      />
    );

    if (isConfirmed) {
      await state.runFlowstep();
    }
  }

  return <>
    <UnsavedChangesDialog
      onSave={() => state.saveFlowstep()}
      isNotSaved={() => state.isNotSaved}
    />
    <TitledPage title={title}>
      <FlowstepContainer>
        <FormControl label={`${sectionNumber++}. Verify range`}>
          <div className={styles.verifyRange}>
            <IconButton
              iconProps={{ iconName: 'xappex-target' }}
              styles={{ icon: { fontSize: '24px' } }}
              onClick={() => handleDetectClick()}
            />
            <span>{state.isDetectingRange
              ? 'detecting range...'
              : state.targetRangeAddress?.addressWithSheet || 'not detected'
            }</span>
          </div>
          <ValidationError
            className={styles.validationError}
            message={!state.isDetectingRange && state.validationErrors.get('target-range-not-selected')}
          />
        </FormControl>
        <FormControl label={
          `${sectionNumber++}.
          Select the object (table) to ${state.operation === Operation.INSERT ? 'insert data to' : 'update data in'}`
        }>
          <HSObjectPicker
            data={state.objectTypes.data}
            value={state.selectedObjectType}
            onChange={(_, newValue) => state.objectTypeName = newValue?.name}
            disabled={state.objectTypes.isLoading}
            errorMessage={state.validationErrors.get('object-type-not-selected')}
          />
        </FormControl>
        <FormControl label={`${sectionNumber++}. Set up mappings`}>
          <PrimaryButton
            className={styles.mappingsButton}
            onClick={() => setMappingManagerVisible(true)}
            disabled={state.isMappingButtonDisabled}
          >
            Mappings
          </PrimaryButton>
          <ValidationError
            className={styles.validationError}
            message={state.validationErrors.get('mapping-error')}
          />
          <ModalPage
            isOpen={isMappingManagerVisible}
            title="Mappings"
            onClose={() => setMappingManagerVisible(false)}
            helpLink={undefined}
            component={() => (
              <MappingManager
                objectType={state.objectTypeName || ''}
                mappedHeaders={state.mappedHeaders}
                constants={state.mappedConstants}
                objectFields={state.writableFields}
                renderEditorForType={renderEditorForType}
                onApplyResult={(result) => {
                  state.applyMapping(result);
                  setMappingManagerVisible(false);
                }} />
            )}
          />
        </FormControl>
        <FormControl label={`${sectionNumber++}. Choose data row to ${state.operation === Operation.INSERT ? 'insert' : 'update'}`}>
          <RangeSelector
            rangeToApply={state.rangeToApply}
            flowstepType={FlowstepType.Hubspot}
            headers={state.tableHeaders}
            disabled={false}
            onChange={(value: any, isValid: boolean) => handleRangeChange(value, isValid)}
            hideIdColumnName={true}
            dontResetOnHeaderChange={true}
          />
        </FormControl>
        <GroupPane
          title="Options"
          collapsed={optionsCollapsed}
          onCollapsed={(collapsed: boolean) => setOptionsCollapsed(collapsed)}
          tooltip=""
          className={styles.groupPane}
        >
          <div className={styles.options}>
            {state.operation === Operation.INSERT && (
              <PropertyEdit
                label='Copy result IDs to Worksheet'
                tooltip={`After the insert operation, the IDs assigned to the newly
                created objects will be copied to the specified column in the worksheet`}
              >
                <Checkbox
                  checked={state.copyResultIds}
                  onChange={(_, checked) => state.copyResultIds = !!checked}
                />
              </PropertyEdit>
            )}
            {state.copyResultIds && state.operation === Operation.INSERT && (
              <>
                <FormControl label='Column to copy result IDs to'>
                  <SearchableComboBox
                    options={state.tableHeaders.map(header => ({ key: header, text: header }))}
                    selectedKey={state.columnForId}
                    onChange={(_, selectedItem) => state.columnForId = selectedItem.key}
                    errorMessage={state.validationErrors.get('column-for-id')}
                  />
                </FormControl>
                <FormControl label='Column with unique values'>
                  <SearchableComboBox
                    options={state.tableHeaders.map(header => ({ key: header, text: header }))}
                    selectedKey={state.uniqueValuesColumn}
                    onChange={(_, selectedItem) => state.uniqueValuesColumn = selectedItem.key}
                    errorMessage={state.validationErrors.get('unique-values-column')}
                  />
                </FormControl>
              </>
            )}
            <PropertyEdit
              label="Show operations results"
              tooltip={`Enable, if you would like to see a table alongside your table 
                with data, where results of executed operations are displayed`}
            >
              <Checkbox
                checked={state.showResultsTable}
                onChange={(_, checked) => state.showResultsTable = !!checked}
              />
            </PropertyEdit>
            <PropertyEdit
              label="Don't send hidden rows"
              tooltip={`Enable, if you would like insert or update only visible rows. 
              Rows hidden by filters of manually, will not be sent.`}
            >
              <Checkbox
                checked={state.onlyVisibleRows}
                onChange={(_, checked) => state.onlyVisibleRows = !!checked}
              />
            </PropertyEdit>
            <PropertyEdit
              label="Force formula recalculation when done"
              tooltip={'Recalculates all workbook formulas and pivot tables after execution of this flow step'}
            >
              <Checkbox
                checked={state.recalculateFormulas}
                onChange={(_, checked) => state.recalculateFormulas = !!checked}
              />
            </PropertyEdit>
          </div>
        </GroupPane>
        <GroupPane
          title="Advanced"
          collapsed={advancedCollapsed}
          onCollapsed={(collapsed: boolean) => setAdvancedCollapsed(collapsed)}
          tooltip=""
          className={styles.groupPane}
        >
          <AdvancedSettings
            baseEditorState={state}
            namePlaceholder={state.generatedFlowstepName}
            timestampCellProps={{ getTimestampSheetId }}
          />
        </GroupPane>
        <FlowstepButtons
          editorState={state}
          disabled={state.isFlowstepButtonsDisabled}
          onRunClick={() => handleRunClick()}
        />
      </FlowstepContainer>
    </TitledPage>
  </>;
});
